import type { CityConfig } from "../city-template/types";

export const middletownDeConfig: CityConfig = {
  city: "Middletown",
  state: "Delaware",
  stateAbbr: "DE",
  slug: "middletown-de",
  route: "/cities/middletown-de",
  title: "Emergency Passport Middletown DE | Expedited Passport Southern New Castle County | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Middletown, DE — as fast as 24 hours. Fast passport renewal for southern New Castle County residents in Delaware's fastest-growing community. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Middletown, DE",
  heroSubtitle: "Living in Delaware's fastest-growing community and need a passport fast? Fast Passport Center helps Middletown, Odessa, Townsend, and southern New Castle County residents get their expedited passports as quickly as 24 hours. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20aerial%20view%20of%20Middletown%20Delaware%20suburban%20community%20with%20the%20Appoquinimink%20River%20winding%20through%20green%20farmland%2C%20modern%20residential%20neighborhoods%2C%20Delaware%20countryside%20with%20open%20fields%20and%20scattered%20trees%2C%20warm%20golden%20hour%20lighting%2C%20peaceful%20small-town%20atmosphere%2C%20no%20visible%20text%20or%20people%2C%20photorealistic%20landscape%20photography%20style&width=1800&height=700&seq=middletown-de-hero-001&orientation=landscape",
  heroImageAlt: "Aerial view of Middletown Delaware suburban community along the Appoquinimink River",
  geoPosition: "39.4496;-75.7163",
  geoPlacename: "Middletown, Delaware",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "1111 N Independence Mall W, Philadelphia, PA 19106",
    distance: "Approximately 60–70 minutes from Middletown, DE via DE Route 1 and I-95 N",
  },
  nearbyCitiesZips: [
    { name: "Middletown", zips: "19709, 19734" },
    { name: "Odessa", zips: "19730" },
    { name: "Townsend", zips: "19734" },
    { name: "Smyrna", zips: "19977" },
    { name: "Clayton", zips: "19938" },
    { name: "Kenton", zips: "19955" },
    { name: "Summit Bridge", zips: "19709" },
    { name: "Glasgow", zips: "19702" },
    { name: "Bear", zips: "19701" },
    { name: "Dover", zips: "19901, 19902, 19904" },
    { name: "Camden", zips: "19934" },
    { name: "Wyoming", zips: "19934" },
    { name: "Hartly", zips: "19953" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for Middletown and southern New Castle County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-home-5-line", title: "Fastest-Growing Community", desc: "Serving Middletown, Odessa, Townsend, and surrounding Delaware growth areas" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to Philadelphia" },
  ],
  internalLinks: [
    { name: "Expedited passport in Wilmington, DE", route: "/cities/wilmington-de" },
    { name: "Emergency passport help in Dover, DE", route: "/cities/dover-de" },
    { name: "Urgent passport services in Newark, DE", route: "/cities/newark-de" },
    { name: "Delaware passport services overview", route: "/states/delaware" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Middletown, Delaware remotely via secure registered courier. We do not have a walk-in office in Middletown — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Middletown and southern New Castle County travelers without the long drive to Philadelphia.",
  fpcOfficeDistance: "Serving Middletown, Odessa, Townsend, and southern New Castle County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the Middletown, Delaware area and southern New Castle County. From the rapidly expanding neighborhoods along DE Route 1 to the historic riverfront of Odessa, from Townsend to the rural communities near the Appoquinimink River — we serve all of Middletown's diverse and growing residential communities.",
  nearbyCitiesIntro: "We serve Middletown and nearby communities including Odessa, Townsend, Smyrna, Clayton, Kenton, Hartly, and rural areas across southern New Castle County and northern Kent County. We also serve Bear, Glasgow, and Dover residents who prefer our expedited service over the self-service route.",
  howItWorksIntro: "Most Middletown travelers complete everything online and ship documents overnight. No need to drive 60–70 minutes to Philadelphia or take time off from work.",
  mistakesIntro: "Middletown, Delaware has a unique mix of fast-growing suburban families and long-time rural residents. Here are the most common passport mistakes we see from Middletown travelers.",
  testimonialsIntro: "Middletown, Delaware travelers — from growing families to corporate commuters — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Middletown, DE? Here are the most common ones we hear from southern New Castle County travelers.",
  statePageRoute: "/states/delaware",
  statePageName: "Delaware",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 60–70 min to Philadelphia", "Full day commitment — challenging for families"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for family travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Middletown travelers with immediate departure needs — as fast as 24 hours. Perfect for last-minute family trips and business travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Family travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Middletown adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Middletown address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Middletown residents and families — including first international trips.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Middletown and southern New Castle County residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Middletown families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Middletown Delaware residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Middletown DE", description: "Urgent passport processing for Middletown Delaware travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Middletown DE", description: "Fast passport renewal for Middletown Delaware residents with a previously-issued passport." },
    { name: "First-Time Passport Middletown DE", description: "New passport application services for Middletown Delaware residents applying for their first passport." },
    { name: "Child Passport Middletown DE", description: "Minor passport services for Middletown Delaware families with children under 16." },
  ],
  testimonials: [
    {
      name: "Jennifer L.",
      location: "Middletown, DE",
      rating: 5,
      text: "We needed four passports for our family's cruise to the Caribbean. Fast Passport Center processed all four in under a week. Unbelievable service for a growing family in Middletown.",
      service: "Family Passport Bundle",
    },
    {
      name: "David H.",
      location: "Townsend, DE",
      rating: 5,
      text: "Business trip to London with five days notice. They walked me through every step, reviewed my documents, and delivered my renewed passport in 3 days. Middletown's best kept secret.",
      service: "Passport Renewal",
    },
    {
      name: "Maria G.",
      location: "Odessa, DE",
      rating: 5,
      text: "Lost my passport before a wedding in Mexico. I thought I would have to cancel. Fast Passport Center had a replacement in my hands in 48 hours. I am so grateful.",
      service: "Lost Passport",
    },
    {
      name: "Robert K.",
      location: "Clayton, DE",
      rating: 5,
      text: "First-time passport for my son's school trip to Europe. Fast Passport Center handled everything, from the acceptance facility appointment to the final delivery. Perfect for parents in rural Delaware.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "middletown-de-1",
      name: "Middletown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "60 W Main St",
      city: "Middletown",
      state: "DE",
      zip: "19709",
      phone: "(302) 378-5100",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Middletown, near the historic district",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Middletown and southern New Castle County. Appointments required — book 1–2 weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "middletown-de-2",
      name: "Smyrna Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 S Main St",
      city: "Smyrna",
      state: "DE",
      zip: "19977",
      phone: "(302) 653-3711",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 8 miles south of Middletown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Middletown and northern Kent County residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "middletown-de-3",
      name: "Bear Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "315 Middletown Rd",
      city: "Bear",
      state: "DE",
      zip: "19701",
      phone: "(302) 836-0162",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 12 miles north of Middletown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for northern Middletown residents closer to the Bear-Glasgow corridor.",
      icon: "ri-mail-line",
    },
    {
      id: "middletown-de-4",
      name: "New Castle County Clerk of Peace",
      type: "County Clerk Passport Acceptance",
      address: "800 N French St",
      city: "Wilmington",
      state: "DE",
      zip: "19801",
      phone: "(302) 395-7799",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Approximately 25 miles north of Middletown in Wilmington",
      website: "https://www.nccde.org/",
      websiteLabel: "New Castle County",
      notes: "County-wide facility with full passport acceptance. Extended hours compared to post office locations.",
      icon: "ri-government-line",
    },
    {
      id: "middletown-de-5",
      name: "Kenton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "28 E Commerce St",
      city: "Kenton",
      state: "DE",
      zip: "19955",
      phone: "(302) 653-6341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Serving western Kent County, approximately 15 miles from Middletown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Rural facility for western Kent County residents. Limited hours — call ahead.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited Philadelphia slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 60–70 min drive to Philadelphia", fpc: "No — ship from Middletown" },
    { feature: "Family Travel Support", agency: "No flexibility for group needs", fpc: "Yes — we handle multi-passport cases" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Middletown's growing families often discover their passport needs just weeks before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Middletown and New Castle County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Knowing the Closest Acceptance Facility",
      description: "With Middletown's rapid growth, many new residents do not know where their nearest acceptance facility is. The Middletown Post Office is your primary local option.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Middletown residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in Middletown or Odessa." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near Middletown or Townsend." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Middletown address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Middletown, DE?", a: "Fast Passport Center offers emergency passport processing in Middletown, Delaware in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Philadelphia Passport Agency (60–70 minutes from Middletown) — without you needing to make the drive." },
    { q: "Does Middletown, Delaware have its own passport agency?", a: "No — neither Middletown nor the state of Delaware has a regional passport agency. The closest facility is the Philadelphia Passport Agency, approximately 60–70 minutes from Middletown via DE Route 1 and I-95. Fast Passport Center's courier service means Middletown residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in Odessa or Townsend?", a: "Absolutely. Fast Passport Center serves all of southern New Castle County including Odessa, Townsend, Summit Bridge, and the rural communities along the Appoquinimink River. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Middletown, DE?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do Middletown, Delaware travelers use?", a: "Most Middletown residents fly internationally through Philadelphia International Airport (PHL), approximately 60–70 minutes north via DE Route 1 and I-95. Wilmington Airport (ILG) in New Castle offers limited domestic connections. Baltimore-Washington International (BWI) is an alternative for some travelers. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in Middletown?", a: "Yes. Fast Passport Center handles multi-passport cases for families in Middletown, Odessa, and Townsend. Whether you need child passports, renewals, or first-time applications for the whole family, we coordinate the process to ensure everyone is ready to travel." },
    { q: "Where is the closest passport acceptance facility to Middletown?", a: "The Middletown Post Office at 60 W Main St is the primary local acceptance facility. The Smyrna Post Office at 101 S Main St is also nearby for southern residents. For county-level services, the New Castle County Clerk of Peace in Wilmington at 800 N French St offers extended hours. All require appointments." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Middletown residents should plan ahead, especially for family trips and seasonal travel." },
  ],
  photoLocations: [
    { name: "Walgreens — Middletown", address: "200 S Main St, Middletown, DE 19709", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Middletown", address: "600 N Broad St, Middletown, DE 19709", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Middletown", address: "700 S Main St, Middletown, DE 19709", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Middletown", address: "500 W Main St, Middletown, DE 19709", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    { name: "DMV — Middletown", address: "1099 S Dupont Hwy, Middletown, DE 19709", hours: "Mon–Fri: 8AM–4:30PM", note: "Serves Middletown and southern New Castle County. Appointments recommended.", featured: true },
    { name: "DMV — Dover", address: "3030 S Dupont Hwy, Dover, DE 19901", hours: "Mon–Fri: 8AM–4:30PM", note: "Alternative for Kent County residents. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "60–70 miles from Middletown, DE",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Primary international departure airport for Middletown travelers. 60–70 minute drive north on DE Route 1 and I-95. Major international hub with direct flights to Europe, the Caribbean, and beyond.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: true,
    },
    {
      code: "ILG",
      name: "Wilmington Airport (New Castle)",
      distance: "25 miles from Middletown, DE",
      address: "141 N DuPont Hwy, New Castle, DE 19720",
      note: "Closest airport to Middletown with limited domestic service. No major international connections.",
      flightUrl: "https://www.flyilg.com/",
      flightLabel: "ILG Airport",
      featured: false,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International",
      distance: "60–70 miles from Middletown, DE",
      address: "7062 Elm Rd, Baltimore, MD 21240",
      note: "Alternative option for budget airlines and travelers with flexible routing.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Airport",
      featured: false,
    },
  ],
};